/**
 * Price-list transport.
 *
 * A price list is three things stacked: the list itself (a currency and a
 * validity window), its quantity-break lines (a variant, a minimum quantity
 * and a unit price, each with its own effective dates), and its bindings —
 * which scope the list applies to, and at what priority.
 *
 * Prices are exact decimal strings end to end. Nothing here parses one into a
 * JS number; a price that survives a round trip through a float is not the
 * price anybody agreed to.
 *
 * Concurrency follows the catalog's own policy: creates are `client-key`
 * idempotent, and every PATCH takes a required strong `If-Match`.
 */
import type { components } from "@erp/api-client";

import {
  apiFetchOperational as apiFetch,
  apiRequestOperational as apiRequest,
  type Page,
} from "@/lib/api/http";
import { fetchWithEtag, type WithEtag } from "@/features/shared/ops-api";

type Schemas = components["schemas"];

export type PriceList = Schemas["CatalogPriceList"];
export type PriceListCreate = Schemas["CatalogPriceListCreate"];
export type PriceListPatch = Schemas["CatalogPriceListPatch"];
export type PriceListStatus = Schemas["CatalogPriceListStatus"];
export type PriceListLine = Schemas["CatalogPriceListLine"];
export type PriceListLineCreate = Schemas["CatalogPriceListLineCreate"];
export type PriceListLinePatch = Schemas["CatalogPriceListLinePatch"];
export type PriceListBinding = Schemas["CatalogPriceListBinding"];
export type PriceListBindingCreate = Schemas["CatalogPriceListBindingCreate"];
export type PriceListBindingPatch = Schemas["CatalogPriceListBindingPatch"];
export type PriceListBindingType = Schemas["CatalogPriceListBindingType"];

const MERGE_PATCH = { "Content-Type": "application/merge-patch+json" } as const;

export const PRICE_LIST_STATUSES: readonly PriceListStatus[] = ["draft", "active", "archived"];

/** `base` needs no target: it is the fallback every resolution falls back to.
 *  The other two point at the channel or customer group they price. */
export const BINDING_TYPES: readonly PriceListBindingType[] = ["base", "channel", "customer_group"];

export interface PriceListFilters {
  status?: PriceListStatus;
  currency?: string;
}

export function listPriceLists(
  filters: PriceListFilters = {},
  signal?: AbortSignal,
): Promise<Page<PriceList>> {
  return apiFetch<Page<PriceList>>("/price-lists", {
    query: { ...filters, limit: 200 },
    signal,
  });
}

export function getPriceList(priceListId: string): Promise<WithEtag<PriceList>> {
  return fetchWithEtag<PriceList>(`/price-lists/${encodeURIComponent(priceListId)}`);
}

export function createPriceList(
  input: PriceListCreate,
  idempotencyKey: string,
): Promise<PriceList> {
  return apiFetch<PriceList>("/price-lists", { method: "POST", body: input, idempotencyKey });
}

export async function updatePriceList(
  priceListId: string,
  patch: PriceListPatch,
  ifMatch: string,
): Promise<WithEtag<PriceList>> {
  const { data, etag } = await apiRequest<PriceList>(
    `/price-lists/${encodeURIComponent(priceListId)}`,
    { method: "PATCH", body: patch, ifMatch, headers: { ...MERGE_PATCH } },
  );
  return { data, etag };
}

/** Only an unused draft deletes; an active list is archived instead, which is
 *  why the screen offers Archive far more often than Delete. */
export function deletePriceList(priceListId: string, ifMatch: string): Promise<void> {
  return apiFetch<void>(`/price-lists/${encodeURIComponent(priceListId)}`, {
    method: "DELETE",
    ifMatch,
  });
}

// ── Lines ────────────────────────────────────────────────────────────────

export function listPriceListLines(
  priceListId: string,
  signal?: AbortSignal,
): Promise<Page<PriceListLine>> {
  return apiFetch<Page<PriceListLine>>(`/price-lists/${encodeURIComponent(priceListId)}/lines`, {
    query: { limit: 200 },
    signal,
  });
}

export function createPriceListLine(
  priceListId: string,
  input: PriceListLineCreate,
  idempotencyKey: string,
): Promise<PriceListLine> {
  return apiFetch<PriceListLine>(`/price-lists/${encodeURIComponent(priceListId)}/lines`, {
    method: "POST",
    body: input,
    idempotencyKey,
  });
}

export function getPriceListLine(
  priceListId: string,
  lineId: string,
): Promise<WithEtag<PriceListLine>> {
  return fetchWithEtag<PriceListLine>(
    `/price-lists/${encodeURIComponent(priceListId)}/lines/${encodeURIComponent(lineId)}`,
  );
}

export async function updatePriceListLine(
  priceListId: string,
  lineId: string,
  patch: PriceListLinePatch,
  ifMatch: string,
): Promise<WithEtag<PriceListLine>> {
  const { data, etag } = await apiRequest<PriceListLine>(
    `/price-lists/${encodeURIComponent(priceListId)}/lines/${encodeURIComponent(lineId)}`,
    { method: "PATCH", body: patch, ifMatch, headers: { ...MERGE_PATCH } },
  );
  return { data, etag };
}

export function deletePriceListLine(
  priceListId: string,
  lineId: string,
  ifMatch: string,
): Promise<void> {
  return apiFetch<void>(
    `/price-lists/${encodeURIComponent(priceListId)}/lines/${encodeURIComponent(lineId)}`,
    { method: "DELETE", ifMatch },
  );
}

// ── Bindings ─────────────────────────────────────────────────────────────

export function listPriceListBindings(
  priceListId: string,
  signal?: AbortSignal,
): Promise<Page<PriceListBinding>> {
  return apiFetch<Page<PriceListBinding>>(
    `/price-lists/${encodeURIComponent(priceListId)}/bindings`,
    { query: { limit: 200 }, signal },
  );
}

export function createPriceListBinding(
  priceListId: string,
  input: PriceListBindingCreate,
  idempotencyKey: string,
): Promise<PriceListBinding> {
  return apiFetch<PriceListBinding>(`/price-lists/${encodeURIComponent(priceListId)}/bindings`, {
    method: "POST",
    body: input,
    idempotencyKey,
  });
}

export function getPriceListBinding(
  priceListId: string,
  bindingId: string,
): Promise<WithEtag<PriceListBinding>> {
  return fetchWithEtag<PriceListBinding>(
    `/price-lists/${encodeURIComponent(priceListId)}/bindings/${encodeURIComponent(bindingId)}`,
  );
}

export async function updatePriceListBinding(
  priceListId: string,
  bindingId: string,
  patch: PriceListBindingPatch,
  ifMatch: string,
): Promise<WithEtag<PriceListBinding>> {
  const { data, etag } = await apiRequest<PriceListBinding>(
    `/price-lists/${encodeURIComponent(priceListId)}/bindings/${encodeURIComponent(bindingId)}`,
    { method: "PATCH", body: patch, ifMatch, headers: { ...MERGE_PATCH } },
  );
  return { data, etag };
}

export function deletePriceListBinding(
  priceListId: string,
  bindingId: string,
  ifMatch: string,
): Promise<void> {
  return apiFetch<void>(
    `/price-lists/${encodeURIComponent(priceListId)}/bindings/${encodeURIComponent(bindingId)}`,
    { method: "DELETE", ifMatch },
  );
}
